import {
  InfoSection,
  InfoSection_Info,
  InfoSection_Info_PlacementOption,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/basket/v1/page/basket_sections.types';
import {
  Accordion,
  COLORS,
  IconSize,
  PixelIcon,
  Text,
  mobileOnlyLinaria,
  tabletAndUpLinaria,
} from '@aboutyou/ui-components';
import {isCaseTypeCheck} from '@aboutyou/utils';
import {styled} from '@linaria/react';
import React from 'react';

interface BasketInfoViewProps extends InfoSection {
  placementFilter: InfoSection_Info_PlacementOption;
  iconSize?: keyof typeof IconSize;
}

const quickInfoMap = (
  section: InfoSection_Info,
  index: number,
  iconSize: keyof typeof IconSize = 'XS',
) => {
  const buttonOpened = (
    <>
      {section.icon && (
        <MainIcon size={iconSize} imageUrl={section.icon?.url} />
      )}
      <Text
        color="Grey1"
        size="px14"
        weight="mediumBold"
        dangerouslySetInnerHTML={{__html: section.title}}
        as="div"
      />
    </>
  );

  if (!section.expandableContent) {
    return <ComponentWithoutTitle>{buttonOpened}</ComponentWithoutTitle>;
  }

  return (
    <StyledAccordion
      id={section.title.substring(0, 5).replace(/\s+/g, '-')}
      key={index}
      content={buttonOpened}
      toggleContent={
        <Text
          color="Grey1"
          size="px14"
          weight="mediumBold"
          dangerouslySetInnerHTML={{__html: section.expandableContent}}
          as="div"
        />
      }
    />
  );
};

export const BasketInfoView: React.FC<BasketInfoViewProps> = ({
  placementFilter,
  type,
  iconSize,
}) => {
  if (
    !isCaseTypeCheck('informationPack', type) ||
    !type.informationPack.sections
  ) {
    return null;
  }
  const {sections} = type.informationPack;
  const filteredSections = sections.filter(
    section => section.placement === placementFilter,
  );

  if (!filteredSections.length) {
    return null;
  }

  /*
  RoundedPanels will help to group the infosection entries on desktop
  Expectation is that the first X entry will be grouped together
  The last entry is always separate.
  */

  return (
    <BasketInfoViewWrapper
      data-testid="infoSectionContainer"
      data-no-padding={
        placementFilter ===
        InfoSection_Info_PlacementOption.ABOVE_CHECKOUT_SECTION
      }
    >
      {filteredSections.length > 1 && (
        <RoundedPanel data-testid="infoSectionGroupedContainer">
          {filteredSections
            .slice(0, -1)
            .map((section: InfoSection_Info, index: number) =>
              quickInfoMap(section, index, iconSize),
            )}
        </RoundedPanel>
      )}

      <RoundedPanel data-testid="infoSectionStandaloneContainer">
        {filteredSections
          .slice(-1)
          .map((section: InfoSection_Info, index: number) =>
            quickInfoMap(section, index, iconSize),
          )}
      </RoundedPanel>
    </BasketInfoViewWrapper>
  );
};

const BasketInfoViewWrapper = styled.div<{$noTopPadding?: boolean}>`
  ${mobileOnlyLinaria(`
    padding: 0.75rem 0;
    background-color: ${COLORS.Grey4};
  `)}

  &[data-no-padding="true"] {
    ${mobileOnlyLinaria(`
      padding: 0 0 0.75rem;
      background-color: ${COLORS.Grey4};
    `)}
  }
`;

const RoundedPanel = styled.div`
  ${tabletAndUpLinaria(`
    border-radius: 4px;
    margin-block: 0.75rem;

    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.04),
      0 10px 15px 0 rgba(0, 0, 0, 0.04);
  `)}
`;

const MainIcon = styled(PixelIcon)`
  margin-right: 16px;
`;

const ComponentWithoutTitle = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  text-align: left;
  background-color: ${COLORS.White};
  padding: 16px;

  & > :last-child {
    margin-left: auto;
  }

  ${tabletAndUpLinaria(`
    border-radius: 5px;
    border: solid 1px ${COLORS.Grey3};
  `)}
`;

const StyledAccordion = styled(Accordion)`
  && {
    border-radius: 5px;
  }
`;
